import type { RefObject } from "react";
import {
  Send,
  Loader2,
  Square,
  Paperclip,
  X,
  FileCode,
} from "lucide-react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { runDevAgentContextCompress } from "./runDevAgentContextCompress";
import type { UploadResult } from "../devAgentService";
import { ACCEPTED_FILE_ATTR, ACCEPTED_FILE_LABEL } from "./constants";

export function AgentFeedComposer({
  input,
  setInput,
  sendingMessage,
  uploadedFiles,
  setUploadedFiles,
  uploading,
  fileInputRef,
  disabled,
  isRunning,
  streamingContent,
  onStop,
  handleSend,
  handleFileUpload,
  totalTokens,
}: {
  input: string;
  setInput: (v: string) => void;
  sendingMessage: boolean;
  uploadedFiles: UploadResult[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadResult[]>>;
  uploading: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  disabled?: boolean;
  isRunning: boolean;
  streamingContent: string | null | undefined;
  onStop?: () => void;
  handleSend: (msg?: string) => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  totalTokens: number;
}) {
  const pendingFile = useDevAgentStore((s) => s.pendingFile);
  const isBusy = disabled || sendingMessage || isRunning || !!streamingContent;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isBusy) return;
      handleSend();
    }
  };

  return (
    <div className="border-t border-border p-3 shrink-0 mx-auto w-full">
        {(pendingFile || uploadedFiles.length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {pendingFile && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-xs text-blue-400">
                <FileCode className="w-3 h-3" />
                <span className="truncate max-w-[160px]">{pendingFile.name}</span>
                {pendingFile.metadataType && (
                  <span className="text-[10px] text-blue-400/60">
                    {pendingFile.metadataType}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => useDevAgentStore.getState().setPendingFile(null)}
                  disabled={isBusy}
                  className="hover:text-blue-300 transition-colors disabled:opacity-50"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {uploadedFiles.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-xs text-green-400"
              >
                <FileCode className="w-3 h-3" />
                <span className="truncate max-w-[160px]">{f.filename}</span>
                <span className="text-[10px] text-green-400/60">
                  {f.lines} lines
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setUploadedFiles((prev) => prev.filter((_, j) => j !== i))
                  }
                  className="hover:text-green-300 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          {/* Upload button — temporarily disabled, integration planned
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_ATTR}
            className="hidden"
            onChange={handleFileUpload}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={
              uploading || isBusy
            }
            className="p-2.5 rounded-xl border-2 border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            title={`Upload file — accepted: ${ACCEPTED_FILE_LABEL}`}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Paperclip className="w-4 h-4" />
            )}
          </button>
          */}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            data-agent-input
            placeholder={
              isRunning || streamingContent
                ? "Agent is working... (Esc to stop)"
                : "Type a message... (⌘K to focus)"
            }
            disabled={isBusy}
            rows={1}
            className="flex-1 resize-none bg-background border-2 border-border rounded-xl px-4 py-2.5 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/60 disabled:opacity-50 transition-shadow"
          />
          {(isRunning || streamingContent) && onStop ? (
            <button
              type="button"
              onClick={onStop}
              className="p-2.5 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              title="Stop"
            >
              <Square className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleSend()}
              disabled={!input.trim() || isBusy}
              className="p-2.5 rounded-xl border-2 border-primary/80 bg-primary text-primary-foreground shadow-md hover:bg-primary/90 hover:border-primary disabled:opacity-45 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              title="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
        </div>
        {totalTokens > 0 && (
          <div className="flex items-center justify-end mt-1">
            <button
              type="button"
              onClick={() => void runDevAgentContextCompress()}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
              title={`${totalTokens.toLocaleString()} tokens used — click to compress context`}
            >
              <div
                className={`w-2 h-2 rounded-full ${totalTokens > 150000 ? "bg-red-500 animate-pulse" : totalTokens > 100000 ? "bg-yellow-500" : totalTokens > 50000 ? "bg-orange-400" : "bg-green-500"}`}
              />
              <span>{Math.round(totalTokens / 1000)}K</span>
            </button>
          </div>
        )}
    </div>
  );
}
